import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchMe } from "../api";
import { getSessionToken, clearSessionToken } from "../utils/auth";
import SignInForm from "./SignInForm";

// Gate for admin-only pages. Resolves the viewer before rendering anything
// privileged, then calls children(me).
//
// This is convenience and clarity, not the security boundary - every admin
// endpoint enforces the ADMIN_EMAILS allowlist server-side. Hiding controls here
// just avoids showing things that would only 403.
function RequireAdmin({ title = "Admin", children }) {
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);

  const signedIn = Boolean(getSessionToken());
  const isAdmin = Boolean(me?.is_admin);

  const checkAccess = useCallback(async () => {
    if (!getSessionToken()) {
      setMe(null);
      setChecking(false);
      return;
    }
    try {
      setMe(await fetchMe());
    } catch {
      // An unusable session shouldn't leave the page stuck "checking".
      clearSessionToken();
      setMe(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    checkAccess();
    window.addEventListener("auth-changed", checkAccess);
    return () => window.removeEventListener("auth-changed", checkAccess);
  }, [checkAccess]);

  useEffect(() => {
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    return () => robots.remove();
  }, []);

  if (checking) {
    return (
      <div className="container mt-5">
        <p className="text-muted">Checking access...</p>
      </div>
    );
  }

  if (!signedIn) {
    return (
      <div className="container mt-5" style={{ maxWidth: 480 }}>
        <h2 className="h4">Admin sign-in</h2>
        <p className="text-muted small">This page is restricted to administrators.</p>
        <div className="card shadow-sm">
          <div className="card-body">
            <SignInForm blurb="Sign in with your admin email address. We'll send you a one-time link - there's no password to remember." />
          </div>
        </div>
        <p className="mt-3">
          <Link to="/">← Back to rosters</Link>
        </p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="container mt-5" style={{ maxWidth: 520 }}>
        <h2 className="h4">{title}</h2>
        <div className="alert alert-danger" role="alert">
          <strong>You don't have admin access.</strong>
          <div className="small mt-1">
            Signed in as {me?.email}. Ask an administrator to add this address to the
            server's allowlist.
          </div>
        </div>
        <p>
          <Link to="/notifications" className="btn btn-sm btn-outline-primary me-2">
            My notifications
          </Link>
          <Link to="/" className="btn btn-sm btn-outline-secondary">
            Back to rosters
          </Link>
        </p>
      </div>
    );
  }

  return children(me);
}

export default RequireAdmin;
